import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import PageLoadingShell from "../layout/PageLoadingShell";
import ListPageSkeleton from "../layout/ListPageSkeleton";
import { encodeID, formatDisplayLongDate, getFromStorage, setDateFormat } from "../../utils/Helper";
import { LanguageConstants } from "../../constants/LanguageConstants";
import {
    getCustomerOrderStatusKey,
    getCustomerPaymentStatusColorClass,
    getCustomerPaymentStatusKey,
} from "../../utils/orderStatus";
import {
    apiStatusForOrderListTab,
} from "../../utils/orderListTabFilter";
import { Modal } from "react-bootstrap";
import OrderCancelMessage from "../../Modal/OrderCancelMessage";
import OrderCancel from "../../Modal/OrderCancel";
import MessageHistoryModal from "../../Modal/MessageHistoryModal";
import Shippingdetails from "../../Modal/Shippingdetails";
import "../myquotation/MyQuotation.scss";
import "./customerOrderStatus.scss";
import flightIcon from "../../Assets/images/flight.svg";
import flight1Icon from "../../Assets/images/flight1.svg";
import { StorageConstant } from "../../constants/Constants";

const ORDERS_LIST_PAGE_SIZE = 10;

/** List API: `1` = in transit, `2` = delivered — both use the highlighted plane asset. */
const isFlight1IconOrderStatus = (status: unknown) => {
    const s = String(status ?? "");
    return s === "1" || s === "2" || s === "6";
};

function Order() {
    const OrderList = useMakeRequest();
    const messageHistoryReq = useMakeRequest();
    const [status, setStatus] = useState("");
    const [activeStatus, setActiveStatus] = useState("");
    const [search, setSearch] = useState("");
    const [start_date, setStartDate] = useState<any>("");
    const [end_date, setEndDate] = useState<any>("");
    const formRef = useRef<HTMLFormElement>(null);
    const [searchStatus, setSearchStatus] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [rows, setRows] = useState<any[]>([]);
    const [listCount, setListCount] = useState(0);
    const [statusLoadingValue, setStatusLoadingValue] = useState<string | null>(null);
    const [quoteIdLoading, setQuoteIdLoading] = useState(false);
    const [dateFilterApplyLoading, setDateFilterApplyLoading] = useState(false);
    const [dateFilterResetLoading, setDateFilterResetLoading] = useState(false);
    const [pendingCloseDateFilter, setPendingCloseDateFilter] = useState(false);
    const [messageshow, setMessageShow] = useState(false);
    const [cancelShow, setCancelShow] = useState(false);
    const [sendData, setsendData] = useState({});
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: ORDERS_LIST_PAGE_SIZE,
    });
    const navigate = useNavigate();
    const [historyData, setHistoryData] = useState({});
    const [messageRows, setMessageRows] = useState<any[]>([]);
    const [msgLoadingId, setMsgLoadingId] = useState<string | number | null>(null);
    const [show, setShow] = useState(false);
    const [showShipping, setShowShipping] = useState(false);
    const [datePanelOpen, setDatePanelOpen] = useState(false);
    /** Same tick as starting a request, `loading` in effects is still false — use ref to clear spinners only after load finishes. */
    const prevOrderLoadingRef = useRef(false);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const scrollLoadGateRef = useRef({
        loading: false,
        hasMore: false,
        pageIndex: 0,
    });

    const displayRows = rows;

    useEffect(() => {
        const payload = {
            status: apiStatusForOrderListTab(status),
            start_date: setDateFormat(start_date),
            end_date: setDateFormat(end_date),
            search: search?.trim(),
            page: pagination.pageIndex,
            pageSize: pagination.pageSize,
        };
        OrderList.makeRequest({ payload, url: ApiConstants?.OrderGet, toastStatus: false });
    }, [searchStatus, status, pagination]);

    useEffect(() => {
        const loading = Boolean(OrderList?.loading);
        const finished = prevOrderLoadingRef.current === true && loading === false;
        prevOrderLoadingRef.current = loading;

        if (!finished) {
            return;
        }

        setStatusLoadingValue(null);
        setQuoteIdLoading(false);
        setDateFilterApplyLoading(false);
        setDateFilterResetLoading(false);

        if (OrderList?.status) {
            setActiveStatus(status);
            setHasLoadedOnce(true);
            if (pendingCloseDateFilter) {
                setDatePanelOpen(false);
                setPendingCloseDateFilter(false);
            }
        } else if (pendingCloseDateFilter) {
            setPendingCloseDateFilter(false);
        }
    }, [OrderList?.loading, OrderList?.status, status, pendingCloseDateFilter]);

    useEffect(() => {
        if (!OrderList?.status) {
            return;
        }
        // Keep previous rows while a status-tab refetch is in flight (same as My Quotes).
        // Server already filters via apiStatusForOrderListTab in the request payload.
        const newRows = OrderList?.data?.data?.rows ?? [];
        const apiCount = Number(OrderList?.data?.data?.count) || 0;
        setListCount(apiCount);
        setRows((prev) => {
            if (pagination.pageIndex === 0) {
                return newRows;
            }
            if (newRows.length === 0) {
                setListCount((c) => Math.min(c, prev.length));
                return prev;
            }
            const seen = new Set(prev.map((r: any) => r?.uuid ?? r?.id));
            const appended = newRows.filter((r: any) => {
                const k = r?.uuid ?? r?.id;
                if (k == null || seen.has(k)) return false;
                seen.add(k);
                return true;
            });
            return appended.length ? [...prev, ...appended] : prev;
        });
    }, [OrderList?.status, OrderList?.data, pagination.pageIndex]);

    scrollLoadGateRef.current = {
        loading: Boolean(OrderList?.loading),
        hasMore: listCount > 0 && rows.length < listCount,
        pageIndex: pagination.pageIndex,
    };

    useEffect(() => {
        const el = loadMoreSentinelRef.current;
        if (!el || !hasLoadedOnce || listCount <= 0) {
            return;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry?.isIntersecting) return;
                const g = scrollLoadGateRef.current;
                if (g.loading || !g.hasMore) return;
                setPagination((p) => ({
                    ...p,
                    pageIndex: p.pageIndex + 1,
                }));
            },
            { root: null, rootMargin: "120px", threshold: 0 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [hasLoadedOnce, listCount, rows.length, OrderList.loading]);

    const handleStartDateInput = (e: any) => {
        setStartDate(e);
    };

    const handleEndDateInput = (e: any) => {
        setEndDate(e);
    };

    const handleSearch = (e: any) => {
        e.preventDefault();
        const hasDate = Boolean(start_date || end_date);
        if (hasDate) {
            setDateFilterApplyLoading(true);
            setPendingCloseDateFilter(true);
        } else {
            setQuoteIdLoading(true);
        }
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        setSearchStatus(!searchStatus);
    };

    const handleReset = () => {
        if (formRef.current) {
            formRef.current.reset();
        }
        setStartDate("");
        setEndDate("");
        setSearch("");
        setDateFilterResetLoading(true);
        setPendingCloseDateFilter(true);
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        setSearchStatus(!searchStatus);
    };

    const getOrderRowKey = (row: any) => {
        const raw = row?.uuid ?? row?.order_id ?? row?.id;
        if (raw == null || String(raw).trim() === "") return null;
        return String(raw).trim();
    };

    const handleNavigate = (row: any) => {
        const orderKey = getOrderRowKey(row);
        if (!orderKey) return;
        const enc = encodeID(orderKey);
        if (enc == null || enc === "") return;
        navigate(`/order-detail/${encodeURIComponent(String(enc))}`);
    };

    const getOrderQuotationId = (row: any) =>
        row?.quotation_id ?? row?.quotation_detail?.quotation_id ?? null;

    const handleMessageHistory = (e: any) => {
        const qid = getOrderQuotationId(e);
        if (qid == null || String(qid).trim() === "") {
            toast.error(LanguageConstants?.NO_REQUEST_FOUND ?? "No request found.");
            return;
        }
        setMsgLoadingId(qid);
        setHistoryData(e);
        setMessageRows([]);
        messageHistoryReq.makeRequest({
            payload: { quotation_id: qid },
            url: ApiConstants?.QuotesMessageHistory,
            toastStatus: false,
        });
    };

    useEffect(() => {
        if (!messageHistoryReq?.status) return;
        const rows = messageHistoryReq?.data?.data;
        setMessageRows(Array.isArray(rows) ? rows : []);
        setShow(true);
        setMsgLoadingId(null);
    }, [messageHistoryReq?.status]);

    useEffect(() => {
        if (!messageHistoryReq?.loading && msgLoadingId != null && !messageHistoryReq?.status) {
            setMsgLoadingId(null);
        }
    }, [messageHistoryReq?.loading, messageHistoryReq?.status, msgLoadingId]);

    const pillClass = (value: string) =>
        `nav-link${activeStatus === value ? " active" : ""}`;

    const formatOrderDate = (d: any) => formatDisplayLongDate(d, "—");

    /** Match My Quotes `getItemsCount`: API `item_count` / fallbacks / count `quotation_detail.quotes_detail`. */
    const orderItemsCount = (res: any) => {
        const fromQuotesDetail =
            Array.isArray(res?.quotation_detail?.quotes_detail) ? res.quotation_detail.quotes_detail.length : undefined;
        const n =
            res?.items_count ??
            res?.item_count ??
            res?.quotes_count ??
            res?.total_items ??
            fromQuotesDetail;
        if (n === 0 || n) return String(n);
        return "—";
    };

    const paymentStatusLabel = (key: ReturnType<typeof getCustomerPaymentStatusKey>) => {
        if (key === "wt_pending") {
            return getFromStorage({ key: StorageConstant?.Translate }) === "spa"
                ? "TB Pendiente"
                : LanguageConstants?.ORDER_PAYMENT_WT_PENDING;
        }
        if (key === "wt_refund_pending") {
            return LanguageConstants?.ORDER_PAYMENT_WT_REFUND_PENDING;
        }
        if (key === "declined") return LanguageConstants?.ORDER_PAYMENT_DECLINED;
        if (key === "pending" || key === "processing") return LanguageConstants?.PENDING;
        if (key === "paid_cc") return `${LanguageConstants?.PAID} ${LanguageConstants?.CC}`;
        if (key === "paid_wt") return `${LanguageConstants?.PAID} ${LanguageConstants?.WT}`;
        if (key === "refunded") return LanguageConstants?.REFUND;
        return LanguageConstants?.REJECT;
    };

    const paymentCell = (res: any) => {
        const key = getCustomerPaymentStatusKey(res);
        return (
            <td className="text-center">
                <span className={`customer-order-status ${getCustomerPaymentStatusColorClass(key)}`}>
                    {paymentStatusLabel(key)}
                </span>
            </td>
        );
    };

    const orderStatusLabel = (key: ReturnType<typeof getCustomerOrderStatusKey>) => {
        if (key === "pending") return LanguageConstants?.PENDING;
        if (key === "in_process") return LanguageConstants?.Progress;
        if (key === "in_transit") return LanguageConstants?.INTRANSIT;
        if (key === "delivered") return LanguageConstants?.DELIVERD;
        return LanguageConstants?.CANVELLED;
    };

    const deliveryCell = (res: any) => {
        const key = getCustomerOrderStatusKey(res);
        const className =
            key === "delivered"
                ? "approved-text"
                : key === "cancelled"
                    ? "red-text"
                    : key === "pending"
                        ? "my-quotes__status--quoting"
                    : key === "in_process"
                        ? "my-quotes__status--draft"
                        : key === "in_transit"
                            ? "customer-order-status customer-order-status--cyan"
                            : "my-quotes__status--quoted";
        return (
            <td className="text-center">
                <span className={className}>{orderStatusLabel(key)}</span>
            </td>
        );
    };

    const dateFilterPanelBusy =
        dateFilterApplyLoading ||
        dateFilterResetLoading ||
        (OrderList.loading && pendingCloseDateFilter);

    const showInitialPageLoader =
        !hasLoadedOnce &&
        (OrderList.loading || (!OrderList.status && OrderList.error == null));

    return (
        <>
            <PageLoadingShell
                loading={showInitialPageLoader}
                skeleton={<ListPageSkeleton />}
                className="head-space"
            >
                <div className="inner-content">
                    <div className="my-quotes">
                            <div className="container">
                                <div className="page-box">
                                    <div className="page-box-inner">
                                        <form ref={formRef} onSubmit={handleSearch} className="my-quotes__toolbar">
                                            <div className="my-quotes__toolbarRow">
                                                <div className="my-quotes__searchGroup">
                                                    <div className="my-quotes__search">
                                                            {quoteIdLoading ? (
                                                                <span className="my-quotes__searchSpinner" aria-hidden />
                                                            ) : (
                                                                <i className="icon-search" aria-hidden />
                                                            )}
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={LanguageConstants?.ORDER_SEARCH_QUOTE_ID}
                                                                value={search}
                                                                onChange={(e: any) => setSearch(e?.target?.value)}
                                                                aria-label={LanguageConstants?.ORDER_SEARCH_QUOTE_ID}
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="my-quotes__calendarBtn"
                                                            aria-expanded={datePanelOpen}
                                                            aria-controls="orders-date-panel"
                                                            disabled={dateFilterPanelBusy && datePanelOpen}
                                                            aria-disabled={dateFilterPanelBusy && datePanelOpen}
                                                            onClick={() => {
                                                                if (dateFilterPanelBusy && datePanelOpen) {
                                                                    return;
                                                                }
                                                                setDatePanelOpen((o) => !o);
                                                            }}
                                                        >
                                                            <i className="icon-calendar-2" aria-hidden />
                                                            <span className="visually-hidden">
                                                                {LanguageConstants?.FROM} / {LanguageConstants?.TO}
                                                            </span>
                                                        </button>
                                                    </div>

                                                {datePanelOpen ? (
                                                    <div
                                                        id="orders-date-panel"
                                                        className="my-quotes__datePopover"
                                                        role="region"
                                                        aria-label={LanguageConstants?.DATETYPE}
                                                        aria-busy={dateFilterApplyLoading || dateFilterResetLoading}
                                                    >
                                                        <div className="my-quotes__dateGrid">
                                                            <div className="my-quotes__dateField com-input-wrap">
                                                                <label className="form-label ms-2">{LanguageConstants?.FROM}</label>
                                                                <DatePicker
                                                                    className="form-control"
                                                                    placeholderText={LanguageConstants?.DATETYPE}
                                                                    timeFormat="MM DD YYYY"
                                                                    selected={start_date}
                                                                    maxDate={new Date()}
                                                                    onKeyDown={(e: any) => e.preventDefault()}
                                                                    onChange={handleStartDateInput}
                                                                />
                                                            </div>
                                                            <div className="my-quotes__dateField com-input-wrap">
                                                                <label className="form-label ms-2">{LanguageConstants?.TO}</label>
                                                                <DatePicker
                                                                    className="form-control"
                                                                    placeholderText={LanguageConstants?.DATETYPE}
                                                                    timeFormat="MM DD YYYY"
                                                                    disabled={start_date ? false : true}
                                                                    minDate={start_date}
                                                                    selected={end_date}
                                                                    maxDate={new Date()}
                                                                    onKeyDown={(e: any) => e.preventDefault()}
                                                                    onChange={handleEndDateInput}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="my-quotes__dateActions mb-1">
                                                            <button
                                                                type="submit"
                                                                className={`round-btn my-quotes__dateActionBtn my-quotes__dateActionBtn--primary ${dateFilterApplyLoading ? "pointer-events-none" : ""}`}
                                                                disabled={
                                                                    OrderList.loading &&
                                                                    (dateFilterApplyLoading || dateFilterResetLoading)
                                                                }
                                                            >
                                                                {dateFilterApplyLoading ? (
                                                                    <span className="my-quotes__dateBtnSpinner my-quotes__dateBtnSpinner--primary" aria-hidden />
                                                                ) : (
                                                                    LanguageConstants?.Filter
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`round-btn my-quotes__dateActionBtn my-quotes__dateActionBtn--primary ${dateFilterResetLoading ? "pointer-events-none" : ""}`}
                                                                onClick={() => handleReset()}
                                                                disabled={
                                                                    OrderList.loading &&
                                                                    (dateFilterApplyLoading || dateFilterResetLoading)
                                                                }
                                                            >
                                                                {dateFilterResetLoading ? (
                                                                    <span className="my-quotes__dateBtnSpinner my-quotes__dateBtnSpinner--primary" aria-hidden />
                                                                ) : (
                                                                    LanguageConstants?.Reset
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </form>

                                        <div className="quotation-box my-quotes__box">
                                            <ul className="nav nav-pills my-quotes__tabs" id="orders-status-pills" role="tablist">
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        type="button"
                                                        className={pillClass("")}
                                                        disabled={OrderList.loading && statusLoadingValue === ""}
                                                        aria-busy={OrderList.loading && statusLoadingValue === ""}
                                                        onClick={() => {
                                                            setStatusLoadingValue("");
                                                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                                                            setStatus("");
                                                        }}
                                                    >
                                                        {OrderList.loading && statusLoadingValue === "" ? (
                                                            <span className="my-quotes__tabSpinner" aria-hidden />
                                                        ) : (
                                                            LanguageConstants?.ALL
                                                        )}
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        type="button"
                                                        className={pillClass("4")}
                                                        disabled={OrderList.loading && statusLoadingValue === "4"}
                                                        onClick={() => {
                                                            setStatusLoadingValue("4");
                                                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                                                            setStatus("4");
                                                        }}
                                                    >
                                                        {OrderList.loading && statusLoadingValue === "4" ? (
                                                            <span className="my-quotes__tabSpinner" aria-hidden />
                                                        ) : (
                                                            LanguageConstants?.Progress
                                                        )}
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        type="button"
                                                        className={pillClass("1")}
                                                        disabled={OrderList.loading && statusLoadingValue === "1"}
                                                        onClick={() => {
                                                            setStatusLoadingValue("1");
                                                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                                                            setStatus("1");
                                                        }}
                                                    >
                                                        {OrderList.loading && statusLoadingValue === "1" ? (
                                                            <span className="my-quotes__tabSpinner" aria-hidden />
                                                        ) : (
                                                            LanguageConstants?.INTRANSIT
                                                        )}
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        type="button"
                                                        className={pillClass("2")}
                                                        disabled={OrderList.loading && statusLoadingValue === "2"}
                                                        onClick={() => {
                                                            setStatusLoadingValue("2");
                                                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                                                            setStatus("2");
                                                        }}
                                                    >
                                                        {OrderList.loading && statusLoadingValue === "2" ? (
                                                            <span className="my-quotes__tabSpinner" aria-hidden />
                                                        ) : (
                                                            LanguageConstants?.DELIVERD
                                                        )}
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        type="button"
                                                        className={pillClass("3")}
                                                        disabled={OrderList.loading && statusLoadingValue === "3"}
                                                        onClick={() => {
                                                            setStatusLoadingValue("3");
                                                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                                                            setStatus("3");
                                                        }}
                                                    >
                                                        {OrderList.loading && statusLoadingValue === "3" ? (
                                                            <span className="my-quotes__tabSpinner" aria-hidden />
                                                        ) : (
                                                            LanguageConstants?.CANVELLED
                                                        )}
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        type="button"
                                                        className={pillClass("0")}
                                                        disabled={OrderList.loading && statusLoadingValue === "0"}
                                                        onClick={() => {
                                                            setStatusLoadingValue("0");
                                                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                                                            setStatus("0");
                                                        }}
                                                    >
                                                        {OrderList.loading && statusLoadingValue === "0" ? (
                                                            <span className="my-quotes__tabSpinner" aria-hidden />
                                                        ) : (
                                                            LanguageConstants?.PENDING
                                                        )}
                                                    </button>
                                                </li>
                                            </ul>

                                            <div className="manual-quote-table-wrap">
                                                <div className="table-responsive my-quotes__tableScroll">
                                                    <table className="table my-quotes__table">
                                                        <thead>
                                                            <tr>
                                                                <th className="text-center">{LanguageConstants?.ORDERID}</th>
                                                                <th className="text-center">{LanguageConstants?.ORDER_COL_ORDER_STATUS}</th>
                                                                <th className="text-center">{LanguageConstants?.DATE}</th>
                                                                <th className="text-center">{LanguageConstants?.MANUAL_DRAFTS_COL_ITEMS}</th>
                                                                <th className="text-center">{LanguageConstants?.ORDER_COL_PMT_STATUS}</th>
                                                                <th className="text-center">{LanguageConstants?.ORDER_COL_MSG}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {displayRows?.length > 0 ? (
                                                                displayRows.map((res: any, i: number) => (
                                                                    <tr
                                                                        key={res?.uuid ?? res?.id ?? i}
                                                                        className="my-orders__row"
                                                                        onClick={() => handleNavigate(res)}
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter" || e.key === " ") {
                                                                                e.preventDefault();
                                                                                handleNavigate(res);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <td className="text-center">
                                                                            <span className="my-orders__orderIdBtn">
                                                                                {getOrderRowKey(res) ?? "—"}
                                                                            </span>
                                                                        </td>
                                                                        {deliveryCell(res)}
                                                                        <td className="text-center">{formatOrderDate(res?.created_at)}</td>
                                                                        <td className="text-center">{orderItemsCount(res)}</td>
                                                                        {paymentCell(res)}
                                                                        <td className="text-center">
                                                                            <div className="d-inline-flex align-items-center justify-content-center gap-2">
                                                                                <button
                                                                                    type="button"
                                                                                    className="my-quotes__msgBtn"
                                                                                    disabled={
                                                                                        msgLoadingId === getOrderQuotationId(res) &&
                                                                                        messageHistoryReq.loading
                                                                                    }
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleMessageHistory(res);
                                                                                    }}
                                                                                    aria-label={LanguageConstants?.MESSAGE}
                                                                                >
                                                                                    {msgLoadingId === getOrderQuotationId(res) &&
                                                                                    messageHistoryReq.loading ? (
                                                                                        <span className="my-quotes__msgSpinner" aria-hidden />
                                                                                    ) : (
                                                                                        <span className="my-quotes__msgIcon" aria-hidden />
                                                                                    )}
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className={[
                                                                                        "my-quotes__msgBtn",
                                                                                        "my-orders__flightBtn",
                                                                                        isFlight1IconOrderStatus(res?.status) ? "my-orders__flightBtn--transit" : "",
                                                                                        res?.shipping_company == null ? "my-orders__flightBtn--disabled" : "",
                                                                                    ]
                                                                                        .filter(Boolean)
                                                                                        .join(" ")}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (res?.shipping_company != null) {
                                                                                            setShowShipping(true);
                                                                                            setsendData(res);
                                                                                        }
                                                                                    }}
                                                                                    disabled={res?.shipping_company == null}
                                                                                    aria-label={LanguageConstants?.TRACKING}
                                                                                >
                                                                                    <img
                                                                                    src={flight1Icon}
                                                                                        // src={
                                                                                        //     isFlight1IconOrderStatus(res?.status)
                                                                                        //         ? flight1Icon
                                                                                        //         : flightIcon
                                                                                        // }
                                                                                        alt=""
                                                                                        width={16}
                                                                                        height={16}
                                                                                    />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={6}>
                                                                        <p className="text-center mt-3 mb-3">{LanguageConstants?.NO_ORDER_DATA}</p>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {displayRows.length > 0 && rows.length < listCount ? (
                                                    <div
                                                        ref={loadMoreSentinelRef}
                                                        style={{ height: 1, width: "100%" }}
                                                        aria-hidden
                                                    />
                                                ) : null}
                                                {OrderList.loading && hasLoadedOnce && displayRows.length > 0 ? (
                                                    <div className="text-center py-3" aria-busy="true">
                                                        <span className="my-quotes__tabSpinner" aria-hidden />
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </div>
                </div>
            </PageLoadingShell>

            <Modal show={messageshow}>
                <OrderCancelMessage setMessageShow={setMessageShow} setCancelShow={setCancelShow} data={sendData} />
            </Modal>

            <Modal show={cancelShow}>
                <OrderCancel setCancelShow={setCancelShow} data={sendData} />
            </Modal>

            <Modal show={show}>
                <MessageHistoryModal
                    rows={messageRows}
                    onClose={() => setShow(false)}
                    titleLabel={LanguageConstants?.ORDERID}
                    titleId={getOrderRowKey(historyData) ?? (historyData as any)?.uuid}
                    hidePartNumber
                />
            </Modal>

            <Modal show={showShipping}>
                <Shippingdetails setShow={setShowShipping} data={sendData} />
            </Modal>
        </>
    );
}

export default Order;
